import differenceInDays from 'date-fns/differenceInDays'
import format from 'date-fns/format'

import { twentyEightDayPeriodMap } from 'data/billing-period/billingPeriodSelectors'
import { INVOICE_TYPE } from 'settings/constants/billing'
import { FRONTEND_DATE_FORMAT_DATE_FNS } from 'settings/constants/date'
import { createDateFromString, isNotInvalidDate } from 'utils/date'

export interface InvoicePeriodLabelInput {
  startDate?: string | null
  endDate?: string | null
  invoiceDate?: string | null
  type?: string | null
}

export interface InvoicePeriodLabel {
  label: string
  dateRange: string | null
}

const isLeapYear = (year: number): boolean => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0

// The year-end period (12/3) always spans 29 days (13 × 28 = 364 < 365, so it stretches to
// Dec 31); the 2/26 period spans 29 days only when it absorbs Feb 29; all others are 28 days
const generatedPeriodLength = (scheduleKey: string, year: number): number => {
  if (scheduleKey === '12/3') return 29
  if (scheduleKey === '2/26' && isLeapYear(year)) return 29
  return 28
}

const isValidDate = (value: Date | null): value is Date => isNotInvalidDate(value)

// Label derivation must never throw during render: tolerate non-string inputs and ISO datetime strings
const parseDateString = (value?: string | null): Date | null =>
  typeof value === 'string' && value ? createDateFromString(value.split('T')[0]) : null

const fallbackLabel = (invoiceDate?: string | null, start?: Date | null): string => {
  const invoiceDateValue = parseDateString(invoiceDate)
  if (isValidDate(invoiceDateValue)) return format(invoiceDateValue, FRONTEND_DATE_FORMAT_DATE_FNS)
  return start && isValidDate(start) ? format(start, 'MMMM yyyy') : '-'
}

// The secondary label shown next to the profile name in the invoice-group headers: the period's
// actual day span, kept consistent for every shape (drop the end month when it repeats the start)
const daySpan = (start: Date, end: Date): string => {
  if (start.getFullYear() !== end.getFullYear()) return `${format(start, 'MMM d, yyyy')} - ${format(end, 'MMM d, yyyy')}`
  if (start.getMonth() === end.getMonth()) return `${format(start, 'MMM d')} - ${format(end, 'd')}`
  return `${format(start, 'MMM d')} - ${format(end, 'MMM d')}`
}

const deriveSpanShape = (start: Date, end: Date): InvoicePeriodLabel => {
  const lastDayOfEndMonth = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate()
  const isMonthAligned = start.getDate() === 1 && end.getDate() === lastDayOfEndMonth
  const monthSpan = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1

  if (isMonthAligned) {
    if (monthSpan === 1) {
      return { label: format(start, 'MMMM yyyy'), dateRange: daySpan(start, end) }
    }

    if (monthSpan === 3 && start.getMonth() % 3 === 0) {
      return { label: format(start, 'QQQ yyyy'), dateRange: daySpan(start, end) }
    }

    // Every other whole-month span (incl. calendar years) uses the app's existing
    // "MMM yyyy - MMM yyyy" period pattern, matching the Create Invoice period picker
    return { label: `${format(start, 'MMM yyyy')} - ${format(end, 'MMM yyyy')}`, dateRange: daySpan(start, end) }
  }

  // Heuristic: an exact 28-day-schedule span is labeled by period number even without knowing the
  // profile; the map is shared with the profile-driven selectors and mirrors the backend schedule
  // (TwentyEightDayBillingScheduleGenerator; a copy lives in file-generator/utils/date.ts)
  const dayCount = differenceInDays(end, start) + 1
  const scheduleKey = `${start.getMonth() + 1}/${start.getDate()}`
  const periodNumber = (twentyEightDayPeriodMap as Record<string, string>)[scheduleKey]
  if (periodNumber && dayCount === generatedPeriodLength(scheduleKey, start.getFullYear())) {
    return {
      label: `Period ${periodNumber}`,
      dateRange: `${format(start, 'M/d/yyyy')} - ${format(end, 'M/d/yyyy')}`,
    }
  }

  const isSameYear = start.getFullYear() === end.getFullYear()
  const label = isSameYear
    ? `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`
    : `${format(start, 'MMM d, yyyy')} - ${format(end, 'MMM d, yyyy')}`
  return { label, dateRange: daySpan(start, end) }
}

/**
 * Derives the invoice period label purely from the invoice's own persisted dates,
 * so the label can never change when the account's billing profile changes.
 */
export const deriveInvoicePeriodLabel = ({ startDate, endDate, invoiceDate, type }: InvoicePeriodLabelInput): InvoicePeriodLabel => {
  const start = parseDateString(startDate)
  const end = parseDateString(endDate)

  if (!isValidDate(start) || !isValidDate(end) || start.getTime() > end.getTime()) {
    return { label: fallbackLabel(invoiceDate, start), dateRange: null }
  }

  const shape = deriveSpanShape(start, end)

  // MANUAL invoices are titled by invoice date, but keep the span-derived secondary date range
  if (type === INVOICE_TYPE.MANUAL) {
    return { label: fallbackLabel(invoiceDate, start), dateRange: shape.dateRange }
  }

  return shape
}
